import { $, el } from '../state/store.js';

/* "Desktop only" — said once, on arrival, on a device that cannot do the job.

   The app needs a mouse for two things that have no touch equivalent:
   dragging four corners onto a photograph precisely enough for the solve to
   mean anything, and orbiting a 3D scene while judging depth. Neither
   degrades gracefully — they just produce a wrong answer more slowly. Saying
   so up front costs a phone visitor ten seconds; not saying so costs them the
   whole session before they work it out.

   A wall, not a warning. It used to be dismissible, on the reasoning that
   people use phones in unpredictable ways — but the two things this app is
   for cannot be done with a finger at all, so "continue anyway" only bought
   someone the chance to discover that themselves, slowly, and form a view of
   the product from the one device it cannot serve. Better to say so once,
   clearly, and send them somewhere that is worth their time.

   Note what this is NOT: a block on small windows, or on browsers, or on
   anything a person can change. It is a block on an input device that cannot
   do the task. Attach a mouse and the app opens.
*/

/* Screen WIDTH is not the question — a narrow desktop window is not a phone,
   and a tablet in landscape is wider than some laptops. The question is what
   the pointer can do.

   `pointer: coarse` means the primary input is a finger. `hover: none` means
   it cannot hover, which is what rules out a laptop with a touchscreen: those
   report a fine pointer AND hover, because a mouse is still there. A device
   matching both has no way to do the precise dragging this app is built on.

   userAgentData.mobile is a second, independent signal — Chromium's own
   answer to "is this a phone" — so a device that reports either is treated as
   touch-only. */
export function isTouchOnly() {
  try {
    if (navigator.userAgentData?.mobile === true) return true;
    /* `any-pointer: fine` is the escape hatch: it is true if ANY attached
       input is precise, so a tablet with a trackpad, a phone with a
       Bluetooth mouse, or a convertible in tablet mode with a pen all fall
       out of this test even though their PRIMARY pointer is coarse. Warning
       someone who has a mouse plugged in would be exactly the second-guessing
       this module's header says to avoid. */
    return matchMedia('(pointer: coarse) and (hover: none)').matches
      && !matchMedia('(any-pointer: fine)').matches;
  } catch (_) {
    return false;
  }
}

export function showMobileNotice() {
  const w = el('div', 'authwrap');
  w.id = 'mobilewrap';
  /* Hidden, not removed. Removing it looked tidier and broke the page: work
     already in flight from module load — the entitlements fetch, the auth
     session — resolves later and paints into elements it expects to still
     exist, so tearing the DOM out from under it threw and tripped the fatal
     error screen. display:none keeps every node addressable while showing
     none of it, and also takes the whole subtree out of the accessibility
     tree, so a screen reader does not read out an app that cannot be used. */
  document.querySelector('.app')?.classList.add('hidden');

  const card = el('div', 'authcard mobilecard');
  card.appendChild(el('h2', null, 'Illusio 3D needs a desktop'));
  /* The same words the landing page's Quick Answers use. Someone who read it
     there and came anyway should meet the identical sentence, not a second,
     differently-worded version that reads like a different rule. */
  card.appendChild(el('p', 'authnote',
    'Desktop only. You need a mouse to calibrate the image accurately and to navigate in the 3D space.'));
  card.appendChild(el('p', 'note',
    'Open illusio3d.com on a computer with a mouse or trackpad and it will work. A tablet with a trackpad attached is fine too.'));

  /* One route, and it goes somewhere useful. A dead end with nothing but an
     apology is the worst version of this screen. */
  const row = el('div', 'authrow');
  const back = el('a', 'btn btn--pri', 'See what Illusio 3D does');
  back.href = 'https://illusio3d.com';
  row.appendChild(back);
  card.appendChild(row);

  w.appendChild(card);
  document.body.appendChild(w);
}

export function initMobileNotice() {
  if (!isTouchOnly()) return;
  showMobileNotice();
}
